'use client';

import React, { useState } from 'react';
import { useCartStore, CartItem } from '@/ui/store/cart';
import { paths } from '@/constants/paths';
import { useProductRuleService } from '@/ui/providers/product-rules-provider';
import { ProductBadge } from '@/ui/components/product-badge/product-badge';
import { CLUB_EXCLUSIVE_BADGE_KEY } from '@/models/product-badge-model';
import CartProductImage from '../CartProductImage';
import RemovalConfirmation from '../RemovalConfirmation';
import QuantityDisplay from '../ProductQuantityDisplay';
import CartProductInfo from '../CartProductInfo';
import { CartItemVariant } from '../types/CartItemVariant';

export interface CartItemComponentProps {
  product: CartItem;
  disableUpdate?: boolean;
  currency?: string;
  variant?: CartItemVariant;
}

export function CartItemComponent({
  product,
  disableUpdate,
  variant,
  currency,
}: Readonly<CartItemComponentProps>) {
  const { updateItemQuantity, removeItem, toggleCartDisplay } = useCartStore();
  const clubBadge = useProductRuleService()
    .getVariantBadges({ productId: product.id, sku: product.sku })
    .find(({ key }) => key === CLUB_EXCLUSIVE_BADGE_KEY);
  const [isMarkedForRemoval, setIsMarkedForRemoval] = useState(false);

  const updateQuantity = (quantity: number) => {
    if (quantity > 0) {
      updateItemQuantity(product.cartItemId, quantity);
      setIsMarkedForRemoval(false); // Cancel removal if quantity is increased
    } else {
      setIsMarkedForRemoval(true); // Mark for removal if quantity is zero or less
    }
  };

  const confirmRemoval = () => removeItem(product.cartItemId);
  const productBasePrice = product.priceVariant?.price || 0;
  return (
    <div className={`flex ${isMarkedForRemoval ? 'bg-neutral-5' : ''} px-4 py-3 sm:px-6`}>
      <CartProductImage
        image={product.image}
        productName={product.name}
        productPath={`${paths.productById}/${product.id}`}
        toggleCartDisplay={disableUpdate ? undefined : toggleCartDisplay}
        quantity={product.quantity}
        variant={variant}
      />
      <div className="ml-4 flex flex-1 flex-col justify-between">
        <div>
          <CartProductInfo
            name={product.name}
            price={productBasePrice}
            discount={product.discount}
            url={`${paths.productById}/${product.id}`}
            nukiId={product.nukiId}
            toggleCartDisplay={disableUpdate ? undefined : toggleCartDisplay}
            subscription={product.subscription}
            currency={currency}
            variant={variant}
            quantity={product.quantity}
          />
          {clubBadge && (
            <ProductBadge
              label={clubBadge.label}
              color={clubBadge.color}
              icon={clubBadge.icon}
              className="mb-3"
            />
          )}
        </div>
        {isMarkedForRemoval ? (
          <RemovalConfirmation
            cancelRemoval={() => setIsMarkedForRemoval(false)}
            confirmRemoval={confirmRemoval}
          />
        ) : (
          <QuantityDisplay
            quantity={product.quantity}
            disableUpdate={disableUpdate}
            fixedQuantity={product.fixedQuantity}
            updateQuantity={updateQuantity}
          />
        )}
      </div>
    </div>
  );
}
